import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import {
  QueryServiceOptionsFactory,
  useModelService,
} from "@shared/services/api/hooks/useModelService";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { Identifier, PaginatorContract } from "@shared/services/api/types";
import { AxiosError } from "axios";
import { useMemo } from "react";
import {
  UseBaseQueryOptions,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import {
  GetCalendarEventsParamsType,
  PublicCalendarEventSerializer,
  PublicVenuePaymentConfigSerializer,
  UpcomingEventsRequestType,
} from "../types/public-venue";

const usePublicVenueService = () => {
  const repository = new PublicBasedVenuesRepository();
  const queryClient = useQueryClient();
  const queryKeyManager = new QueryKeyManager(repository);

  const { useList, useRetrieve, useGenericMutation, useGenericQuery } =
    useModelService(repository);

  const useContactVenue = (venueId: number) => {
    return useGenericMutation<undefined, { id: Identifier }>(
      "contact",
      "POST",
      { injects: { id: venueId } }
    );
  };

  /**
   * Fetch venue payment configuration (e.g. Stripe publishable key) independently from event caching.
   *
   * Event detail pages may be served from Redis cache, but payment config is volatile and must be fetched
   * client-side so it doesn't get embedded into the cached event payload.
   */
  const usePublicVenuePaymentConfig = (
    venueId: number | undefined,
    queryOptions?: QueryServiceOptionsFactory<PublicVenuePaymentConfigSerializer>["options"]
  ) => {
    return useGenericQuery<PublicVenuePaymentConfigSerializer>(
      "paymentConfig",
      {
        injects: { id: venueId },
        options: {
          enabled: Boolean(venueId),
          staleTime: 60 * 1000,
          refetchOnWindowFocus: false,
          ...queryOptions,
        },
      }
    );
  };

  const usePublicCalendarEvents = (
    params: GetCalendarEventsParamsType,
    // TODO allow for more than just queryOptions
    queryOptions?: QueryServiceOptionsFactory<
      PaginatorContract<PublicCalendarEventSerializer>
    >["options"]
  ) => {
    return useGenericQuery<PaginatorContract<PublicCalendarEventSerializer>>(
      "calendar",
      {
        injects: { slug: params.slug },
        params: {
          ...params,
          version: 1,
        },
        options: {
          enabled: Boolean(params.slug),
          staleTime: 60 * 10000,
          refetchOnWindowFocus: false,
          ...queryOptions,
        },
      }
    );
  };

  const usePublicUpcomingEvents = (
    venueSlug: string,
    params: UpcomingEventsRequestType,
    // TODO allow for more than just queryOptions
    queryOptions?: UseBaseQueryOptions<
      PublicBaseEventSerializer[],
      AxiosError | unknown
    >
  ) => {
    const { data: upcomingEvents, ...rest } = useGenericQuery<
      PublicBaseEventSerializer[]
    >("upcomingEvents", {
      injects: { slug: venueSlug },
      params,
      options: {
        keepPreviousData: true,
        staleTime: 60 * 10000,
        refetchOnWindowFocus: false,
        ...queryOptions,
      },
    });

    const events = useMemo(() => {
      if (!upcomingEvents) return [];

      const cleanedEvents = [...upcomingEvents];

      // Clean up image data
      return cleanedEvents.map((event: PublicBaseEventSerializer) => {
        if (event.thumbnail === null) {
          return {
            ...event,
            image: undefined,
            image_banner: undefined,
          };
        }
        return event;
      });
    }, [upcomingEvents]);

    return {
      ...rest,
      upcomingEvents: events,
    };
  };

  const prefetchCalendarMonthEvents = async (
    params: GetCalendarEventsParamsType,
    // TODO allow for more than just queryOptions
    queryOptions?: UseQueryOptions<
      PaginatorContract<PublicCalendarEventSerializer>,
      AxiosError | unknown
    >
  ) => {
    if (!params.slug) return;

    const queryKey = queryKeyManager.generateKey(
      ["calendar", JSON.stringify(params)],
      { slug: params.slug }
    );

    await queryClient.prefetchQuery(
      queryKey,
      () =>
        repository.calendar({
          ...params,
          version: 1,
        } as GetCalendarEventsParamsType & {
          slug: string;
        }),
      queryOptions
    );
  };

  return {
    useList,
    useRetrieve,
    useContactVenue,
    usePublicVenuePaymentConfig,
    usePublicCalendarEvents,
    usePublicUpcomingEvents,
    prefetchCalendarMonthEvents,
  };
};

export default usePublicVenueService;
